import React, { useState, useRef } from 'react'
import styled from 'styled-components'
import { repeat, update } from 'ramda'
import { Box, CloseButton, Flex, Button } from 'pcln-design-system'
import { Plus as PlusIcon, Minus as MinusIcon } from 'pcln-icons'
import { AirPricedCalendar } from '@pcln/fly-date-picker'
import { NestDataObject } from 'react-hook-form'
import CalendarWrapperBox from '../../CalenderWrapperBox'
import DateField from '../../DateField'
import ShadowEffect from '../../ShadowEffect'
import TypeAhead from '../../TypeAhead'
import { PopoverContainer } from '../../Popover'
import {
  setFlights,
  removeFlight,
  addFlight,
  getPrevDate,
  flightsStartLocationSearchKey,
  flightsEndLocationSearchKey,
  flightsDateRangeKey
} from './utils'
import { getDateRange } from '../../../utils/date-helper'
import {
  getDateFieldErrorMessage,
  getDateFieldBorderColors
} from '../../../utils/error-helpers'
import {
  FlightRecordType,
  FlightFormStateType,
  FlightDateType,
  SetValueFunctionType
} from '../types'
import { LOCATION_SEARCH_TYPE } from '../../../types'
import { MULTI_DESTINATION } from '../constants'

const Close = styled(CloseButton)`
  padding: 12px;
  height: 48px !important;
`

type MultiDestinationProps = {
  flights: Partial<FlightRecordType>[]
  errors: NestDataObject<FlightFormStateType>
  setValue: SetValueFunctionType<FlightFormStateType>
  isMobile: boolean
}

// Needed in order to remove the prop type errors caused by
// using js component and typescript
const TypeAheadJS: any = TypeAhead
const DateFieldJS: any = DateField

export default function MultiDestination({
  flights,
  errors,
  setValue,
  isMobile
}: MultiDestinationProps) {
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean[]>(
    repeat(false, 4)
  )
  const calendarInput = useRef<HTMLInputElement | null>(null)

  const focusCalendarInput = (): void => {
    if (calendarInput && calendarInput.current) {
      calendarInput.current.focus()
    }
  }

  const typeaheadWidth = [1, null, 1 / 2, null, 1 / 3]
  const startLocationPlaceholder = 'Departing from?'
  const endLocationPlaceholder = 'Going to?'

  const datePickerLabel = 'Departing'
  return (
    <>
      {flights.slice(1).map((flight, index) => {
        const flightIndex = index + 1
        const datePickerAriaLabel = `Departing Date Picker ${flightIndex + 1}`
        const startLocationSearchKey = flightsStartLocationSearchKey(
          flightIndex
        )
        const endLocationSearchKey = flightsEndLocationSearchKey(flightIndex)
        const dateRangeKey = flightsDateRangeKey(flightIndex)
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Flex key={flightIndex} mx={-2} flexWrap="wrap">
            <Box width={typeaheadWidth} p={2}>
              <ShadowEffect enabled forProperty={startLocationSearchKey}>
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <TypeAheadJS
                    searchProduct="flights"
                    placeholder={startLocationPlaceholder}
                    label={startLocationPlaceholder}
                    searchKey={startLocationSearchKey}
                    errors={errors}
                    defaultSelectedItem={flight.startLocation}
                    disableShadowState={disableShadowState}
                    onItemSelect={(item: LOCATION_SEARCH_TYPE) => {
                      setFlights(
                        flights,
                        {
                          startLocation: item
                        },
                        setValue,
                        flightIndex
                      )
                    }}
                  />
                )}
              </ShadowEffect>
            </Box>
            <Box width={typeaheadWidth} p={2}>
              <ShadowEffect enabled forProperty={endLocationSearchKey}>
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <TypeAheadJS
                    searchProduct="flights"
                    placeholder={endLocationPlaceholder}
                    label={endLocationPlaceholder}
                    searchKey={endLocationSearchKey}
                    errors={errors}
                    defaultSelectedItem={flight.endLocation}
                    disableShadowState={disableShadowState}
                    onItemSelect={(item: LOCATION_SEARCH_TYPE) => {
                      setFlights(
                        flights,
                        {
                          endLocation: item
                        },
                        setValue,
                        flightIndex
                      )
                    }}
                  />
                )}
              </ShadowEffect>
            </Box>
            <Flex
              alignItems="center"
              justifyContent="stretch"
              width={[1, null, null, null, 1 / 3]}
              px={2}
              py={2}
            >
              <ShadowEffect forProperty={dateRangeKey}>
                {({
                  disableShadowState
                }: {
                  disableShadowState: () => void
                }) => (
                  <PopoverContainer
                    key="dates"
                    data-calendar
                    open={isCalendarOpen[index]}
                    position="right"
                    onDismiss={() => {
                      setIsCalendarOpen(update(index, false, isCalendarOpen))
                      disableShadowState()
                    }}
                  >
                    <Box>
                      <DateFieldJS
                        m={0}
                        width={1 / 2}
                        id={dateRangeKey}
                        name={dateRangeKey}
                        ref={(elem: HTMLInputElement) => {
                          calendarInput.current = elem
                        }}
                        label={datePickerLabel}
                        placeholder={datePickerLabel}
                        value={getDateRange(
                          {
                            departureDate: flight.startDate ?? null,
                            arrivalDate: flight.endDate ?? null
                          },
                          true
                        )}
                        onFocus={() => {
                          setIsCalendarOpen(update(index, true, isCalendarOpen))
                          // analytics call for handleCalendarOnFocus(`datefield ${i}`)
                        }}
                        error={getDateFieldErrorMessage(errors, flightIndex)}
                        errorZIndex={1}
                        hideTooltip={
                          isCalendarOpen[index] || !!flight.startDate
                        }
                        isMobile={isMobile}
                        color={getDateFieldBorderColors(errors, flightIndex)}
                        data-testid={`FLIGHTS_TRAVEL_DATES_INPUT_${flightIndex}`}
                        aria-label={datePickerAriaLabel}
                        aria-expanded={isCalendarOpen[index]}
                      />
                    </Box>
                    <CalendarWrapperBox>
                      <AirPricedCalendar
                        monthWidthPx={300}
                        id={`flight-date-range-calendar-${flightIndex}`}
                        onChange={(
                          _e: React.ChangeEvent,
                          dates: FlightDateType
                        ) => {
                          const { startDate = '' } = dates
                          const dateObj = { startDate }
                          setFlights(flights, dateObj, setValue, flightIndex)
                          disableShadowState()
                          setIsCalendarOpen(
                            update(index, false, isCalendarOpen)
                          )
                        }}
                        animateScroll
                        minDate={getPrevDate(flights, flightIndex)}
                        startDate={flight.startDate}
                        selectTwoDates={false}
                        allowSameDay={false}
                        flightDetails={{
                          cabinClass: 'Economy',
                          flights,
                          tripType: MULTI_DESTINATION
                        }}
                        variantName="EXPRESS"
                        useDefaultFooter
                        onDismiss={() => {
                          disableShadowState()
                          setIsCalendarOpen(
                            update(index, false, isCalendarOpen)
                          )
                          focusCalendarInput()
                        }}
                      />
                    </CalendarWrapperBox>
                  </PopoverContainer>
                )}
              </ShadowEffect>
              {flightIndex > 0 && !isMobile && (
                <Close
                  aria-label="remove flight"
                  id="remove-flight-button"
                  ml={2}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault()
                    removeFlight(flights, flightIndex, setValue)
                  }}
                  title="Remove flight"
                />
              )}
            </Flex>
          </Flex>
        )
      })}
      <Box py={2}>
        {isMobile ? (
          <Flex mx={-1} alignItems="center" justifyContent="stretch">
            <Button
              variation="outline"
              aria-label="add flight"
              disabled={flights?.length > 4}
              id="mobile-add-flight-button"
              mx={1}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                addFlight(flights, setValue)
              }}
              size="small"
              width={1 / 2}
            >
              <PlusIcon size={24} />
            </Button>
            <Button
              variation="outline"
              aria-label="remove flight"
              disabled={flights?.length < 2}
              id="mobile-remove-flight-button"
              mx={1}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault()
                removeFlight(flights, flights.length - 1, setValue)
              }}
              size="small"
              width={1 / 2}
            >
              <MinusIcon size={24} />
            </Button>
          </Flex>
        ) : (
          <Button
            variation="outline"
            aria-label="add flight"
            disabled={flights?.length > 4}
            id="add-flight-button"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              addFlight(flights, setValue)
            }}
            pl="12px"
            size="medium"
          >
            <Flex alignItems="center">
              <PlusIcon size={24} />
              Add another flight
            </Flex>
          </Button>
        )}
      </Box>
    </>
  )
}
